import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import { Dialog } from "primereact/dialog";
import * as signalR from "@microsoft/signalr";
import { createSignalRConnection } from "../../utils/signalr";
import PersistentIframeModal from "../PersistentIframeModal/PersistentIframeModal";
import {
  selectCurrentChatAgtId,
  selectCurrentEmpid,
  selectCurrentUser,
  setChatAgtId,
} from "../../pages/login/authSlice";
import "./customCallDialog.scss";

const WebRtcDashboard = () => {
  const dispatch = useDispatch();
  const empId = useSelector(selectCurrentEmpid);
  const CurrentChatAgtId = useSelector(selectCurrentChatAgtId);
  const currentUserName = useSelector(selectCurrentUser);
  const authState = useSelector((state: any) => state.auth);
  const [connection, setConnection] = useState<signalR.HubConnection | null>(
    null
  );
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState<{
    message: string;
    callerId: string;
  } | null>(null);
  const [persistentModalVisible, setPersistentModalVisible] = useState(false);
  const originalTitle = useRef(document.title);

  const [accepted, setAccepted] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [offline, setOffline] = useState(false);

  const [roomId, setRoomId] = useState("");
  const [fileName, setFileName] = useState("");
  const [apiEndPoint, setApiEndPoint] = useState("");
  const [role, setRole] = useState("");
  const [otherUserFullName, setOtherUserFullName] = useState("");

  // Use ref to track timeout and acceptance status
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const acceptedRef = useRef(false);
  const declinedRef = useRef(false);

  const getFullUserName = (userState: any): string => {
    if (authState?.userNameWithId) {
      const nameWithId = authState.userNameWithId;
      const nameOnly = nameWithId.replace(/\s*\(\d+\)/, '').trim();
      const parts = nameOnly.split(',').map((part: string) => part.trim());
      if (parts.length === 2) {
        return `${parts[1]} ${parts[0]}`;
      }
    }
    if (typeof userState === 'string') {
      return userState.replace(/\s*\(Impersonating\)\s*/g, ' ').trim();
    }
    return 'User';
  };

  const userName = getFullUserName(currentUserName);

  // Listen for messages from the WebRTC iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Listen for WebRTC peer connection events
      if (event.data.type === 'PEER_CONNECTED') {
        // Agent has joined the WebRTC room, clear timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        setAccepted(true);
        acceptedRef.current = true;
        setDeclined(false);
        setOffline(false);
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const handleFocus = () => {
      document.title = originalTitle.current;
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // Setup SignalR
  useEffect(() => {
    const conn = createSignalRConnection(empId);
    conn
      .start()
      .then(() => console.log("User connected:", empId))
      .catch((err) => console.error("Connection error:", err));

    // Incoming call
    conn.on("PromptUser", (message: string, callerId: string) => {
      console.log("Incoming call:", message, "from", callerId);
      setOtherUserFullName(message.replace(/\s+is requesting a call$/i, '').trim());

      if (document.visibilityState === "hidden") {
        document.title = "🔔 Incoming Call!";

        const audio = new Audio("/notification.wav");
        audio.play().catch(() => { });

        if (Notification.permission === "granted") {
          const notif = new Notification("Incoming Call", {
            body: message,
            icon: "/CallNotification.svg",
          });
          console.log("Notification displayed:", notif);
        } else {
          Notification.requestPermission();
        }
      }


      if (empId && callerId) {
        setRoomId(`${callerId?.trim()}_${empId.trim()}`);
        setFileName('');
        setRole('callee');
        setApiEndPoint('');
      }
      setModalContent({ message, callerId });
      setModalVisible(true);
      toast.info(`Incoming call: ${message}`);
    });

    // Response from other user
    conn.on("ReceiveResponse", (response: string) => {
      console.log("Response received:", response);
      toast.info(`Response: ${response}`);

      if (response === "Answer") {
        setAccepted(true);
        acceptedRef.current = true;
        setDeclined(false);
        setOffline(false);
        setPersistentModalVisible(false);
        
        setTimeout(() => {
          setPersistentModalVisible(true);
        }, 1000);
        // Clear timeout (backup in case PEER_CONNECTED doesn't fire)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else if (response === "Reject") {
        setAccepted(false);
        setDeclined(true);
        declinedRef.current = true;
        // setPersistentModalVisible(false);
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      } else if (response === "Timeout") {
        // Handle timeout from team lead
        toast.warning("Call timed out - Caller did not wait");
        setModalVisible(false);
        setPersistentModalVisible(false);
        setAccepted(false);
        setDeclined(false);
        setOffline(false);
      }
    });

    setConnection(conn);
    return () => {
      conn.stop().catch((err) => console.error("Error stopping connection:", err));
    };
  }, [empId]);

  const sendResponse = (response: string) => {
    if (connection && modalContent) {
      connection.invoke("SendResponse", modalContent.callerId, response);
      setModalVisible(false);
      toast.success(`Response sent: ${response}`);

      if (response === "Answer") {
        setAccepted(true);
        acceptedRef.current = true;
        setDeclined(false);
        setOffline(false);
        setPersistentModalVisible(false);

        setTimeout(() => {
          setPersistentModalVisible(true);
        }, 1000);
        
      } else if (response === "Reject") {
        setAccepted(false);
        setDeclined(true);
        declinedRef.current = true;
        // setPersistentModalVisible(false);
      }
    }
  };

  // Handle caller initiating call - TIMEOUT LOGIC HERE
  useEffect(() => {
    if (empId && CurrentChatAgtId && CurrentChatAgtId.length > 5) {
      
      // Reset states
      setAccepted(false);
      setDeclined(false);
      setOffline(false);
      acceptedRef.current = false;
      declinedRef.current = false;
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setRoomId(`${empId.trim()}_${CurrentChatAgtId.trim()}`);
      setFileName(`${empId}_${CurrentChatAgtId}`);
      setRole('caller');
      setApiEndPoint(`${encodeURIComponent(`${process.env.REACT_APP_API_URI}`)}`);
      setPersistentModalVisible(true);

      // Create timeout
      timeoutRef.current = setTimeout(() => {
        if (acceptedRef.current) {
          return;
        }
        
        if (declinedRef.current) {
          return;
        }
        
        toast.warning('Call timeout - Agent did not respond');

        if (connection && CurrentChatAgtId) {
          connection.invoke("SendResponse", CurrentChatAgtId, "Timeout")
            .catch((err) => console.error("Error sending timeout notification:", err));
        }

        dispatch(setChatAgtId(null));
        setPersistentModalVisible(false);
        setAccepted(false);
        setDeclined(false);
        setOffline(false);
        sessionStorage.removeItem('chatAgentName');
        timeoutRef.current = null;
      }, 60000);

    } else if (!CurrentChatAgtId) {
      
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      
      setRole('');
      setPersistentModalVisible(false);
      setAccepted(false);
      setDeclined(false);
      acceptedRef.current = false;
      declinedRef.current = false;
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [empId, CurrentChatAgtId, connection, dispatch]);

  useEffect(() => {
    if (CurrentChatAgtId) {
      const agentName = sessionStorage.getItem('chatAgentName');
      if (agentName) {
        setOtherUserFullName(agentName);
      }
    }
  }, [CurrentChatAgtId]);


  useEffect(() => {
    const handleOfflineEvent = (event: any) => {
      setOffline(event.detail.offline);
    };

    window.addEventListener('agentOffline', handleOfflineEvent);

    return () => {
      window.removeEventListener('agentOffline', handleOfflineEvent);
    };
  }, []);

  useEffect(() => {
    const handleIframeMessage = (event: MessageEvent) => {
      if (event.data.type === 'PEER_DISCONNECTED') {
        setOffline(true);
        toast.info('The other user has left the call');
      }
    };

    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, []);

  return (
    <div className="universal-dashboard">
      {persistentModalVisible && (
        <PersistentIframeModal
          key={roomId}
          src={
            `${process.env.REACT_APP_WEBRTC_URL
            }/join?room=${roomId}` +
            `&name=${userName}` +
            `&FileName=${fileName}` +
            `&Role=${role}` +
            `&ApiEndPoint=${apiEndPoint}`
          }
          agentAccepted={accepted}
          agentDeclined={declined}
          agentOffline={offline}
          currentUserName={userName}
          otherUserName={otherUserFullName}
        />
      )}

      <Dialog
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        closable={false}
        style={{ width: "600px", borderRadius: "465.6185302734375px" }}
        className="custom-call-dialog"
        modal
      >
        <div className="call-dialog-body">
          <h2 className="call-title">
            {modalContent?.message || "Incoming call"}
          </h2>
          <div
            style={{
              width: '120px',
              height: '120px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #7D71E2 0%, #0047FF 100%)',
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '2.8rem',
              fontWeight: 600,
              margin: '20px auto',
            }}
          >
            {otherUserFullName
              .split(' ')
              .filter(word => word.length > 0)
              .map(word => word[0].toUpperCase())
              .slice(0, 2)
              .join('')}
          </div>
          <div className="dots-loader">
            <span />
            <span />
            <span />
            <span />
          </div>
          <div className="call-action-buttons">
            <button
              className="call-btn decline"
              onClick={() => sendResponse("Reject")}
            >
              <i className="pi pi-times-circle" /> Decline
            </button>
            <button
              className="call-btn accept"
              onClick={() => sendResponse("Answer")}
            >
              <i className="pi pi-phone" /> Answer
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default WebRtcDashboard;
